var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, InputAdornment, Stack, Typography, styled } from '@mui/material';
import { round } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../../enum';
import { useUserSessionAPI } from '../../app/selectors';
import { FormErrorText, FormField, NumberInput } from '../../common';
import { BasePanel } from '../common/basePanel';
import { BaseVolumeTable } from '../common/baseTable';
import { QueuePanel } from '../common/queuePanel';
import { StepContainer, StepPanel } from '../common/stepContainer';
import { LocationTableEntryMode, WizardStep } from '../constants';
import { LocationTable } from '../locationTable';
import { useQueueLocation } from '../queue/selectors';
import { useEffectiveCapacityPermission, useIsPlanLocked, useWizardParams } from '../selectors';
import { useWizardSimpleContext } from '../useWizardSimpleContext';
import { WAPSection } from '../workArrivalPattern';
import WapTimezoneContext from '../workArrivalPattern/wapContext';
import { LocationSelectionDialog } from '../workVolume/locationSelectionDialog';
var ApplyOccupancyButton = styled(Button)(function (_a) {
    var theme = _a.theme;
    return ({
        minWidth: '78px',
        height: '32px',
        padding: theme.spacing(0, 2),
        borderRadius: theme.shape.borderRadius,
        border: "1px solid ".concat(theme.palette.primary.main),
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.primary.main,
        textAlign: 'center',
        lineHeight: 'normal',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            border: "1px solid ".concat(theme.palette.primary.main),
        },
    });
});
// Occupancy table - reuses BaseVolumeTable with prod hours specific constraints
export var OccupancyTable = function () {
    return (_jsx(BaseVolumeTable, { name: "prodOccupancy", min: 0.01, max: 100, decimal: 2, fixedDecimalScale: false, suffix: "%", addColumns: [] }));
};
export var ProdHoursContent = function () {
    var _a, _b, _c, _d;
    var t = useTranslation('wizard').t;
    var capacityPermission = useEffectiveCapacityPermission();
    var userSession = useUserSessionAPI()[0];
    var queueLocations = useQueueLocation();
    var _e = useFormContext(), control = _e.control, setValue = _e.setValue, getValues = _e.getValues, watch = _e.watch;
    var errors = useFormState({ name: 'queueLocations', control: control }).errors;
    var locationAllocationError = errors.queueLocations;
    var isPlanLocked = useIsPlanLocked();
    var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
    var _f = (plan !== null && plan !== void 0 ? plan : {}).periodLength, periodLength = _f === void 0 ? 0 : _f;
    var step = useWizardParams().step;
    var _g = useState(false), locationDialogOpen = _g[0], setLocationDialogOpen = _g[1];
    var isReview = step === WizardStep.Review;
    var isMultiGeoEnabled = !!((_d = (_c = (_b = userSession.data) === null || _b === void 0 ? void 0 : _b.orgs) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.multiGeoEnabled);
    var entryMode = watch('volumeEntryMode');
    var isPercentage = entryMode === LocationTableEntryMode.Percentage;
    var modeFormat = isPercentage ? { min: 0, max: 100, suffix: '%', decimal: 2 } : { min: 0, max: 999999, decimal: 2 };
    var prevTotalsRef = useRef([]);
    var handleFocus = useCallback(function () {
        var _a, _b;
        var currentTotals = (_b = (_a = getValues('volumes')) === null || _a === void 0 ? void 0 : _a.map(function (v) { return ((v === null || v === void 0 ? void 0 : v.productionHour) == null ? null : Number(v.productionHour)); })) !== null && _b !== void 0 ? _b : [];
        prevTotalsRef.current = currentTotals;
    }, [getValues]);
    var handleBlur = useCallback(function (periodIdx) {
        var _a, _b;
        if (isPercentage)
            return;
        var currentTotals = (_b = (_a = getValues('volumes')) === null || _a === void 0 ? void 0 : _a.map(function (v) { return ((v === null || v === void 0 ? void 0 : v.productionHour) == null ? null : Number(v.productionHour)); })) !== null && _b !== void 0 ? _b : [];
        var prevTotals = prevTotalsRef.current;
        var oldTotal = prevTotals[periodIdx];
        var newTotal = currentTotals[periodIdx];
        if (oldTotal === newTotal || oldTotal == null || newTotal == null || oldTotal === 0)
            return;
        var currentQueueLocations = getValues('queueLocations');
        if (!(currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.length))
            return;
        var hasChanges = false;
        var updated = currentQueueLocations.map(function (loc) {
            var _a, _b;
            return (__assign(__assign({}, loc), { volumes: (_b = (_a = loc.volumes) === null || _a === void 0 ? void 0 : _a.map(function (v, idx) {
                    if (idx !== periodIdx)
                        return v;
                    var rawVal = (v === null || v === void 0 ? void 0 : v.productionHour) == null ? null : Number(v.productionHour);
                    if (rawVal === null)
                        return v;
                    hasChanges = true;
                    return __assign(__assign({}, v), { productionHour: round((rawVal / oldTotal) * newTotal, 2) });
                })) !== null && _b !== void 0 ? _b : [] }));
        });
        if (hasChanges) {
            setValue('queueLocations', updated, { shouldDirty: true });
        }
        prevTotalsRef.current[periodIdx] = newTotal;
    }, [getValues, setValue, isPercentage]);
    var parentVolumes = watch('volumes');
    useEffect(function () {
        if (!isMultiGeoEnabled || !(parentVolumes === null || parentVolumes === void 0 ? void 0 : parentVolumes.length))
            return;
        var currentQueueLocations = getValues('queueLocations');
        if (!(currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.length))
            return;
        var hasChanges = false;
        var updated = currentQueueLocations.map(function (loc) {
            var _a;
            var locVolumes = (_a = loc.volumes) !== null && _a !== void 0 ? _a : [];
            var updatedVolumes = locVolumes.map(function (v, idx) {
                var _a, _b;
                var parentOcc = (_b = (_a = parentVolumes[idx]) === null || _a === void 0 ? void 0 : _a.prodOccupancy) !== null && _b !== void 0 ? _b : 100;
                if (v.prodOccupancy !== parentOcc) {
                    hasChanges = true;
                    return __assign(__assign({}, v), { prodOccupancy: parentOcc });
                }
                return v;
            });
            return __assign(__assign({}, loc), { volumes: updatedVolumes });
        });
        if (hasChanges) {
            setValue('queueLocations', updated, { shouldDirty: true, shouldValidate: true });
        }
    }, [parentVolumes, isMultiGeoEnabled, setValue, getValues]);
    var handleApplyOccupancy = function () {
        var targetOccupancy = getValues('targetOccupancy');
        if (targetOccupancy !== undefined && targetOccupancy !== null) {
            // Update all volumes.prodOccupancy values with the targetOccupancy value
            // while preserving the id field
            for (var i = 0; i < periodLength; i++) {
                var currentVolume = getValues("volumes.".concat(i));
                setValue("volumes.".concat(i), __assign(__assign({}, currentVolume), { prodOccupancy: targetOccupancy }), {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }
            // Also update location-specific volumes
            var currentQueueLocations = getValues('queueLocations');
            if (currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.length) {
                currentQueueLocations.forEach(function (loc, locIdx) {
                    for (var i = 0; i < periodLength; i++) {
                        var locVolume = getValues("queueLocations.".concat(locIdx, ".volumes.").concat(i));
                        setValue("queueLocations.".concat(locIdx, ".volumes.").concat(i), __assign(__assign({}, locVolume), { prodOccupancy: targetOccupancy }), {
                            shouldDirty: true,
                            shouldValidate: true,
                        });
                    }
                });
            }
        }
    };
    return (_jsxs("div", __assign({ "data-testid": "prod-hours-content" }, { children: [_jsx(BaseVolumeTable, { name: "productionHour", averagePrecision: 2, decimal: 2, fixedDecimalScale: false, min: 0, addColumns: ['Total', 'Average'], onFocus: handleFocus, onBlur: handleBlur }), _jsxs(Stack, __assign({ spacing: 2, sx: { mt: 3, mb: 4 } }, { children: [!!queueLocations.length && (_jsxs(Box, { children: [_jsx(Typography, __assign({ variant: "subtitle1", sx: { mb: 2 } }, { children: t('prodHours.productionHoursAllocation') })), _jsx(LocationTable, __assign({ name: "productionHour" }, modeFormat, { entryModeField: "volumeEntryMode", showTypeSums: true, showGroupHeader: true })), !!(locationAllocationError === null || locationAllocationError === void 0 ? void 0 : locationAllocationError.root) && (_jsx(FormErrorText, { message: t('prodHours.locationProdHoursAllocationError') }))] })), isMultiGeoEnabled && !isReview && (_jsx(Box, { children: _jsx(Button, __assign({ variant: "outlined", disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked, onClick: function () { return setLocationDialogOpen(true); }, sx: { textTransform: 'uppercase' } }, { children: t('workVolume.chooseLocations') })) }))] })), !isReview && (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ variant: "subtitle1", sx: { mt: 4 } }, { children: t('prodHours.occupancy') })), _jsxs(Stack, __assign({ direction: "row", spacing: 2, alignItems: "center", sx: { mb: 2 } }, { children: [_jsx(FormField, __assign({ required: true, name: "targetOccupancy", label: "", control: control }, { children: _jsx(NumberInput, { disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked, min: 0.01, max: 100, decimal: 2, fixedDecimalScale: false, defaultValue: 100, InputProps: {
                                        endAdornment: _jsx(InputAdornment, __assign({ position: "end" }, { children: "%" })),
                                    } }) })), _jsx(ApplyOccupancyButton, __assign({ variant: "outlined", onClick: handleApplyOccupancy, disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked }, { children: t('prodHours.apply') }))] })), _jsx(Stack, __assign({ sx: { mt: 3 } }, { children: _jsx(OccupancyTable, {}) }))] })), _jsx(LocationSelectionDialog, { open: locationDialogOpen, onClose: function () { return setLocationDialogOpen(false); } })] })));
};
export var ProdHoursStep = function () {
    var t = useTranslation('wizard', { keyPrefix: 'prodHours' }).t;
    var _a = useState(null), timezone = _a[0], setTimezone = _a[1];
    var timezoneContext = useMemo(function () { return ({ timezone: timezone, setTimezone: setTimezone }); }, [timezone]);
    return (_jsx(WapTimezoneContext.Provider, __assign({ value: timezoneContext }, { children: _jsx(StepContainer, __assign({ leftContent: _jsx(QueuePanel, {}) }, { children: _jsxs(StepPanel, { children: [_jsx(BasePanel, __assign({ title: t('fullName') }, { children: _jsx(ProdHoursContent, {}) })), _jsx(WAPSection, {})] }) })) })));
};
